import { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Activity, ArrowRight, Upload, Brain, Ruler, TrendingUp, BarChart3, Shield, Building2, Heart, School } from "lucide-react";
import useTheme from "@/utils/useTheme";
import ToolPageNavbar from "@/components/sections/ToolPageNavbar";
import Footer from "@/components/sections/Footer";

const IMAGES = {
  hero: "https://customer-assets.emergentagent.com/job_83ea495c-8bdb-4889-9a46-037ad22cb3df/artifacts/hxjiycyn_Hero.jpg",
  card1: "https://customer-assets.emergentagent.com/job_83ea495c-8bdb-4889-9a46-037ad22cb3df/artifacts/sb86xbyt_card-1.jpg",
  card2: "https://customer-assets.emergentagent.com/job_83ea495c-8bdb-4889-9a46-037ad22cb3df/artifacts/5q9aet09_card-2.jpg",
  card3: "https://customer-assets.emergentagent.com/job_83ea495c-8bdb-4889-9a46-037ad22cb3df/artifacts/bqy9pq8h_card-3.jpg",
  features: "https://customer-assets.emergentagent.com/job_83ea495c-8bdb-4889-9a46-037ad22cb3df/artifacts/f7ip8czd_features.png",
  hospital: "/who-we-help-hospital.png",
  ngo: "/who-we-help-ngo.png",
  diagnostic: "/who-we-help-diagnostic.png",
  school: "/who-we-help-school.png",
};

const features = [
  { icon: Upload, title: "X-Ray Upload & AI Analysis", description: "Upload a left-hand radiograph and our ensemble neural network (EfficientNetV2-S, 3-fold) predicts skeletal maturity age within 0.5 years of accuracy.", color: "#4ECDC4" },
  { icon: Ruler, title: "Adult Height Prediction", description: "Using the Bayley-Pinneau method with bone age, parental height, and ethnicity-adjusted population data, we predict final adult height (AHP).", color: "#FFE66D" },
  { icon: TrendingUp, title: "Interactive Growth Curves", description: "Track your child's position on IAP-Khadilkar 2015 percentile growth curves (3rd-97th) — with WHO MGRS used for ages below 5 — for height-for-age and weight-for-age with growth spurt zone visualization.", color: "#A855F7" },
  { icon: BarChart3, title: "Growth Health Index (GHI)", description: "A proprietary composite score (0-100) that combines bone age difference, weight status, and growth remaining into a single actionable metric.", color: "#FF6B6B" },
  { icon: Brain, title: "Maturity Assessment", description: "Determine carpal bone maturity stage, Age at Peak Height Velocity (APHV), and whether skeletal development is advanced, normal, or delayed.", color: "#4ECDC4" },
  { icon: Shield, title: "Clinical PDF Reports", description: "Generate comprehensive, branded clinical reports with X-ray images, growth curves, and all metrics — ready for pediatrician consultation.", color: "#FFE66D" },
];

const whoWeHelp = [
  { icon: Building2, title: "Hospitals", description: "Integrated bone age assessment for pediatric departments", image: IMAGES.hospital },
  { icon: Heart, title: "NGOs", description: "Large-scale child health screening for underserved communities", image: IMAGES.ngo },
  { icon: Activity, title: "Diagnostic Centers", description: "AI-augmented bone age reports for faster turnaround", image: IMAGES.diagnostic },
  { icon: School, title: "Schools", description: "Annual growth and development screening programs", image: IMAGES.school },
];


const howItWorks = [
  { step: "01", title: "Upload X-Ray", description: "Take a standard left-hand posteroanterior X-ray and upload it to our secure platform." },
  { step: "02", title: "AI Bone Age", description: "Our deep learning ensemble analyzes the radiograph and determines skeletal age in seconds." },
  { step: "03", title: "Growth Assessment", description: "Enter height, weight, and parental data. The system calculates predicted adult height and GHI." },
  { step: "04", title: "Download Report", description: "Get a comprehensive clinical PDF with all findings, growth curves, and recommendations." },
];

const BoneAHAboutPage = () => {
  useTheme();
  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <div className="min-h-screen dark bg-mesh">
      <ToolPageNavbar />
      <main className="pt-28 pb-16">
        {/* Hero with bone.jpeg */}
        <section className="max-w-6xl mx-auto px-6 mb-20">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div initial={{ opacity: 0, x: -30 }} animate={{ opacity: 1, x: 0 }}>
              <div className="w-16 h-16 rounded-2xl mb-6 flex items-center justify-center bg-[#4ECDC4]/10 border border-[#4ECDC4]/20">
                <Activity className="w-8 h-8 text-[#4ECDC4]" />
              </div>
              <h1 className="font-display text-4xl md:text-5xl text-white mb-4" data-testid="boneah-about-title">
                Bone <span className="text-[#4ECDC4]">AH</span>
              </h1>
              <p className="text-lg text-white/50 mb-4">Accurate, fast bone age test for pediatric clinics, schools and diagnostic centers.</p>
              <p className="text-base text-white/40 leading-relaxed mb-8">
                BoneAH uses state-of-the-art deep learning to assess skeletal maturity from hand X-rays,
                predict adult height using the Bayley-Pinneau method, and provide a comprehensive Growth Health Index —
                giving pediatricians and parents actionable insights for every child&apos;s growth journey and child development.
              </p>
              <Link to="/tools/boneah" className="inline-flex items-center gap-2 bg-[#4ECDC4] hover:bg-[#3dbdb5] text-[#0a1628] font-semibold px-8 py-4 rounded-xl text-lg transition-colors" data-testid="boneah-try-now-btn">
                Try BoneAH Now <ArrowRight className="w-5 h-5" />
              </Link>
            </motion.div>
            <motion.div initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.2 }}>
              <div className="glass-card p-3 rounded-2xl overflow-hidden">
                <img src={IMAGES.hero} alt="BoneAH - Bone Age Assessment" className="w-full h-80 object-cover rounded-xl" data-testid="boneah-hero-image" />
              </div>
            </motion.div>
          </div>
        </section>

        {/* Card Images Section */}
        <section className="max-w-6xl mx-auto px-6 mb-20">
          <div className="grid md:grid-cols-3 gap-6">
            {[
              { title: "AI-driven support for all decision-making related to healthcare.", image: IMAGES.card1, accent: "#4ECDC4" },
              { title: "Crafted to function as an impeccable collaborator for secure health.", image: IMAGES.card2, accent: "#A855F7" },
              { title: "Set-up and scale anywhere with the finest support.", image: IMAGES.card3, accent: "#FF6B6B" },
            ].map((card, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }}
                className="glass-card overflow-hidden group">
                <img src={card.image} alt={card.title} className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-500" />
                <div className="p-5">
                  <p className="text-sm text-white/60 leading-relaxed">{card.title}</p>
                  <div className="h-1 w-12 rounded-full mt-3" style={{ background: card.accent }} />
                </div>
              </motion.div>
            ))}
          </div>
        </section>

        {/* Features with section-features.png */}
        <section className="max-w-6xl mx-auto px-6 mb-20">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
              <div className="glass-card p-3 rounded-2xl overflow-hidden">
                <img src={IMAGES.features} alt="BoneAH Features" className="w-full rounded-xl" />
              </div>
            </motion.div>
            <div>
              <h2 className="font-display text-3xl text-white mb-6">What BoneAH Offers</h2>
              <div className="space-y-5">
                {features.map((f, i) => (
                  <motion.div key={i} initial={{ opacity: 0, x: 20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.08 }}
                    className="flex items-start gap-4" data-testid={`boneah-feature-${i}`}>
                    <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0" style={{ background: `${f.color}15` }}>
                      <f.icon className="w-5 h-5" style={{ color: f.color }} />
                    </div>
                    <div>
                      <h3 className="font-display text-base text-white mb-1">{f.title}</h3>
                      <p className="text-sm text-white/45 leading-relaxed">{f.description}</p>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* How It Works */}
        <section className="max-w-4xl mx-auto px-6 mb-20">
          <div className="text-center mb-10">
            <h2 className="font-display text-3xl text-white mb-3">How It Works</h2>
            <p className="text-white/40">Four simple steps to a complete growth assessment</p>
          </div>
          <div className="space-y-5">
            {howItWorks.map((s, i) => (
              <motion.div key={i} initial={{ opacity: 0, x: -20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }}
                className="flex items-start gap-5 glass-card p-5" data-testid={`boneah-step-${i}`}>
                <div className="w-12 h-12 rounded-xl bg-[#4ECDC4]/10 flex items-center justify-center shrink-0">
                  <span className="text-lg font-bold text-[#4ECDC4]">{s.step}</span>
                </div>
                <div>
                  <h3 className="font-display text-lg text-white mb-1">{s.title}</h3>
                  <p className="text-sm text-white/50">{s.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </section>

        {/* Why bone age matters — SEO content */}
        <section className="max-w-4xl mx-auto px-6 mb-20" data-testid="why-bone-age-section">
          <h2 className="font-display text-3xl md:text-4xl text-white mb-6 text-center">Why bone age matters</h2>
          <div className="space-y-4 text-white/55 leading-relaxed text-base md:text-lg">
            <p>
              A child&apos;s <strong className="text-white/80">bone age</strong> can differ from their calendar age by up to 2 years —
              and that gap is the single most useful early indicator of growth disorders, premature puberty, growth-hormone deficiency
              and skeletal maturation issues. A timely <strong className="text-white/80">bone age test</strong> helps pediatricians
              decide if a child is on a healthy trajectory or needs closer follow-up.
            </p>
            <p>
              Traditionally, radiologists read hand X-rays manually using Greulich-Pyle or Tanner-Whitehouse atlases — accurate but
              slow, subjective and inconsistent across clinics. BoneAH&apos;s deep-learning model, trained on tens of thousands of
              expert-annotated X-rays, returns a calibrated bone age and final-height prediction in seconds — making accurate
              skeletal-age assessment accessible to every clinic, school screening drive and diagnostic centre.
            </p>
          </div>
        </section>

        {/* Who We Help with images */}
        <section className="max-w-6xl mx-auto px-6 mb-20">
          <div className="text-center mb-10">
            <h2 className="font-display text-3xl text-white mb-3">Who We Help</h2>
            <p className="text-white/40">BoneAH serves healthcare across institutions</p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {whoWeHelp.map((item, i) => {
              const Icon = item.icon;
              return (
                <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }}
                  className="glass-card overflow-hidden group">
                  <img src={item.image} alt={item.title} className="w-full h-44 object-contain bg-[#0A1029] p-2 group-hover:scale-[1.03] transition-transform duration-500" loading="lazy" />
                  <div className="p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <Icon className="w-4 h-4 text-[#4ECDC4]" />
                      <h3 className="font-display text-base text-white">{item.title}</h3>
                    </div>
                    <p className="text-xs text-white/45">{item.description}</p>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </section>

        {/* CTA */}
        <section className="max-w-3xl mx-auto px-6 text-center">
          <Link to="/tools/boneah" className="inline-flex items-center gap-2 bg-[#4ECDC4] hover:bg-[#3dbdb5] text-[#0a1628] font-semibold px-10 py-4 rounded-xl text-lg transition-colors">
            Start Bone Age Assessment <ArrowRight className="w-5 h-5" />
          </Link>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default BoneAHAboutPage;
